import React, { useContext, useMemo } from 'react';
import {
  Colors,
  ColorValues,
  Flex,
  IconChevronRight,
  RemoteImage,
  Typography,
  useScreenSize,
} from '@banhala/ui-kit';
import { useSimilarGoods } from '../../../queries/useSimilarGoods';
import Svg, { Defs, LinearGradient, Path, Rect, Stop } from 'react-native-svg';
import { ScrollContext } from '../../../context/ScrollContext';
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';
import {
  bridge,
  isApp,
  isNil,
  isNotNil,
  isWeb,
  useNavigator,
} from 'shared/src/utils';
import { useGoodsSno } from '../../../hooks/useGoodsSno';
import { useLogClickSimilarGoodsMore } from '../hooks/useLogClickSimilarGoodsMore';
import { useLogDisplayRelatedGoods } from '../hooks/useLogDisplayRelatedGoods';
import { AdAction } from 'shared/src/utils/eventLogger/adLogger/types';
import { buildGoodsAnalyticsParams } from '../utils/buildGoodsAnalyticsParams';
import { buildFirstPageRendering } from '../utils/buildFirstPageRendering';
import { NetworkImageView } from 'native-views';

const PADDING_HORIZONTAL = 16;
const ITEM_SPACING = 8;

export const ITEM_SHOW_COUNT = 3;
export const GRID_ITEM_SHOW_COUNT = 4;

const GRID_VERTICAL_SPACING = 16;

/** 더보기 버튼 높이 — paddingVertical 8*2 + subtitle4 lineHeight 16 + border 1*2 */
const MORE_BUTTON_HEIGHT = 34;

const chunkRows = <T,>(list: T[], size: number): T[][] =>
  Array.from({ length: Math.ceil(list.length / size) }, (_, rowIndex) =>
    list.slice(rowIndex * size, (rowIndex + 1) * size),
  );

const GoodsItemView: React.FC<{
  sno: number;
  title: string;
  price: string;
  image: string;
  discountPercent: string;
  width: number;
  hasAdBadge: boolean;
  onPress: () => void;
}> = props => {
  const height = useMemo(() => props.width * 1.2, [props.width]);

  return (
    <Flex
      position={'relative'}
      width={props.width}
      height={height}
      borderRadius={4}
      overflow={'hidden'}
      onPress={props.onPress}
    >
      {!isWeb ? (
        <NetworkImageView
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          source={{ uri: props.image, doAnimate: false }}
          width={props.width}
          height={height}
          style={{ flex: 1 }}
        />
      ) : (
        <RemoteImage uri={props.image} width={props.width} height={height} />
      )}
      {props.hasAdBadge && (
        <Flex position={'absolute'} top={4} left={4}>
          {/* ui-kit IconAdBadgeOpacity는 배경/글자를 fill 하나로 틴트해서 배경 black 20% + 흰 글자를 표현 못해 직접 그림 */}
          <Svg width={16} height={12} viewBox="0 0 16 12" fill="none">
            <Path
              d="M0 5C0 2.79086 1.79086 1 4 1H12C14.2091 1 16 2.79086 16 5V7C16 9.20914 14.2091 11 12 11H4C1.79086 11 0 9.20914 0 7V5Z"
              fill="#000000"
              fillOpacity={0.2}
            />
            <Path
              d="M3.6875 9H2.60156L4.59375 3.34375H5.84375L7.84375 9H6.75781L6.28516 7.60156H4.15625L3.6875 9ZM4.42969 6.78125H6.01172L5.24219 4.5H5.19531L4.42969 6.78125ZM10.4375 9H8.51562V3.34375H10.4688C12.1562 3.34375 13.1758 4.40234 13.1797 6.16406C13.1758 7.93359 12.1562 9 10.4375 9ZM9.53125 8.11719H10.3828C11.5664 8.12109 12.1641 7.46875 12.1641 6.16406C12.1641 4.86719 11.5664 4.22266 10.4141 4.22656H9.53125V8.11719Z"
              fill="#FFFFFF"
            />
          </Svg>
        </Flex>
      )}
      <Flex
        position={'absolute'}
        left={0}
        right={0}
        bottom={0}
        padding={{ horizontal: 8, vertical: 8 }}
      >
        <Flex position={'absolute'} left={0} right={0} bottom={0}>
          <Svg width={props.width} height={height * 0.4}>
            <Rect
              x={0}
              y={0}
              width={props.width}
              height={height * 0.4}
              fill="url(#paint0_linear_5546_5091)"
              fillOpacity="0.6"
            />
            <Defs>
              <LinearGradient
                id="paint0_linear_5546_5091"
                x1={height * 0.4 + 0.5}
                y1="0"
                x2={height * 0.4 + 0.5}
                y2={height * 0.4}
                gradientUnits="userSpaceOnUse"
              >
                <Stop stopOpacity="0" />
                <Stop offset="1" />
              </LinearGradient>
            </Defs>
          </Svg>
        </Flex>
        <Flex.Row gap={4}>
          <Typography type={'subtitle4'} rawColor={ColorValues.pink20}>
            {props.discountPercent}
          </Typography>
          <Typography type={'subtitle4'} color={'content_always_white'}>
            {props.price}
          </Typography>
        </Flex.Row>
        <Typography
          type={'body5'}
          color={'content_always_white'}
          numberOfLines={1}
          ellipsizeMode={'tail'}
        >
          {props.title}
        </Typography>
      </Flex>
    </Flex>
  );
};

export const RelatedGoodsView: React.FC<{
  focused: boolean;
  isGrid: boolean;
}> = ({ focused = true, isGrid }) => {
  const { scrollY } = useContext(ScrollContext);
  const goodsSno = useGoodsSno();

  const { data: similarGoods, isSuccess, isLoading } = useSimilarGoods(focused);
  const { width } = useScreenSize();

  const itemShowCount = isGrid ? GRID_ITEM_SHOW_COUNT : ITEM_SHOW_COUNT;
  const rowItemCount = isGrid ? 2 : ITEM_SHOW_COUNT;

  const itemWidth = useMemo(() => {
    if (isGrid) {
      // 카드가 상/하 여백 사이 높이(커버=정사각형)를 비율(1:1.2)로 채우도록 역산
      const cardHeight =
        (width -
          GRID_VERTICAL_SPACING * 2 -
          ITEM_SPACING -
          12 -
          MORE_BUTTON_HEIGHT) /
        2;
      return Math.min(
        cardHeight / 1.2,
        (width - PADDING_HORIZONTAL * 2 - ITEM_SPACING) / 2,
      );
    }
    return (
      (width - PADDING_HORIZONTAL * 2 - ITEM_SPACING * 2) / ITEM_SHOW_COUNT
    );
  }, [width, isGrid]);

  const targetList = useMemo(() => {
    if (!isSuccess) return [];
    if (!similarGoods) return [];
    if (similarGoods.goods.length < itemShowCount) return [];

    return similarGoods.goods.slice(0, itemShowCount);
  }, [isSuccess, similarGoods, itemShowCount]);

  const itemRows = useMemo(
    () =>
      chunkRows(
        targetList.map((item, index) => ({ item, index })),
        rowItemCount,
      ),
    [targetList, rowItemCount],
  );
  const { navigator } = useNavigator();

  useLogDisplayRelatedGoods({
    focused: focused,
    listId: similarGoods?.list_id ?? '',
    listParams: similarGoods?.list_params ?? '',
    targetList: targetList ?? [],
    isSuccess,
    isLoading,
  });
  const animStyle = useAnimatedStyle(() => {
    if (!scrollY || scrollY.value > 0) {
      return {};
    }
    return {
      transform: [
        {
          translateY: interpolate(
            scrollY.value,
            [-width, 0],
            [width * 0.5, 0],
            'clamp',
          ),
        },
      ],
    };
  });

  const logClickSimilarGoodsMore = useLogClickSimilarGoodsMore();

  if (isNil(targetList)) return null;

  return (
    <Flex.Center
      position={'absolute'}
      top={0}
      bottom={0}
      left={0}
      right={0}
      padding={{ vertical: isGrid ? GRID_VERTICAL_SPACING : 0 }}
    >
      <Animated.View style={animStyle}>
        <Flex.Center gap={isGrid ? 12 : 20}>
          {isLoading ? (
            <Flex gap={ITEM_SPACING}>
              {chunkRows(
                Array.from({ length: itemShowCount }, (_, i) => i),
                rowItemCount,
              ).map((row, rowIndex) => (
                <Flex.Row key={rowIndex} gap={ITEM_SPACING}>
                  {row.map(item => (
                    <Flex
                      key={item}
                      width={itemWidth}
                      height={itemWidth * 1.2}
                      backgroundColorValue={ColorValues.gray55}
                      borderRadius={4}
                    ></Flex>
                  ))}
                </Flex.Row>
              ))}
            </Flex>
          ) : (
            <Flex gap={ITEM_SPACING}>
              {itemRows.map((row, rowIndex) => (
                <Flex.Row key={rowIndex} gap={ITEM_SPACING}>
                  {row.map(({ item, index }) => (
                    <GoodsItemView
                      sno={item.sno}
                      key={item.sno.toString()}
                      title={item.name}
                      price={item.render.data.price}
                      image={item.image}
                      discountPercent={item.render.data.discount_rate}
                      width={itemWidth}
                      hasAdBadge={isGrid && isNotNil(item.ad)}
                      onPress={() => {
                        bridge.logAnalyticsEvent({
                          name: 'CLICK_GOODS',
                          ably_only_log: item.logging.ably_only_log,
                          analytics: {
                            SCREEN: 'GOODS_DETAIL',
                            LIST_ID: similarGoods?.list_id ?? '',
                            LIST_INDEX: index,
                            LIST_PARAMS: similarGoods?.list_params ?? '',
                            ...buildGoodsAnalyticsParams(item),
                            ...item.logging.analytics,
                          },
                        });

                        if (isNotNil(item.ad)) {
                          bridge.logAdEvent({
                            ad: item.ad,
                            action: AdAction.CLICK,
                          });
                        }
                        navigator.moveToGoods({
                          sno: item.sno,
                          firstPageRendering: buildFirstPageRendering(item),
                          ad: item.ad,
                        });
                      }}
                    />
                  ))}
                </Flex.Row>
              ))}
            </Flex>
          )}
          {isApp() && (
            <Flex.Row
              borderWidth={1}
              borderRadius={6}
              borderColor={'white'}
              padding={{ horizontal: 16, vertical: 8 }}
              gap={4}
              alignItems={'center'}
              onPress={() => {
                navigator.moveToGoodsList(
                  `ably://goods_list/?goods_sno=${goodsSno}&type=similar_goods&position=thumbnail&landing_from=thumbnail`,
                );

                logClickSimilarGoodsMore();
              }}
            >
              <Typography type={'subtitle4'} color={'content_always_white'}>
                관련 상품 더보기
              </Typography>
              <IconChevronRight fill={Colors.content_always_white} size={16} />
            </Flex.Row>
          )}
        </Flex.Center>
      </Animated.View>
    </Flex.Center>
  );
};
